import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';

export default function BrokerSettingsPage() {
  const { profile } = useCompanyProfile();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    phone_number: '',
    first_name: '',
    last_name: '',
  });

  useEffect(() => {
    if (profile) {
      loadFullProfile();
    }
  }, [profile]);

  const loadFullProfile = async () => {
    const { data } = await supabase
      .from('profiles')
.select('company_name, street, city, state, zip_code, phone_number, first_name, last_name, created_at')
      .eq('id', profile?.id)
      .single();

   if (data) {
      if (data.created_at) (profile as any).created_at = data.created_at;
      setForm({
        company_name: data.company_name || '',
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
        phone_number: data.phone_number || '',
        first_name: data.first_name || '',
        last_name: data.last_name || '',
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          company_name: form.company_name,
          street: form.street,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
          phone_number: form.phone_number,
          first_name: form.first_name,
          last_name: form.last_name,
        })
        .eq('id', profile?.id);

      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="max-w-4xl space-y-8">
      {saved && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm font-medium text-green-800">Settings saved successfully!</p>
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {/* Company Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input type="text" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input type="text" value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Code</label>
            <input type="text" value={profile.company_code} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 font-mono" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
            <input type="text" value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input type="text" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
            <input type="text" value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Account Details */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Account Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="text" value={profile.email} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MC Number</label>
            <input type="text" value={profile.mc_number || 'Not set'} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DOT Number</label>
            <input type="text" value={profile.dot_number || 'Not set'} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
          <input type="text" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Subscription</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-700 capitalize">
                {profile.plan || 'Starter'}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                profile.subscription_status === 'active' ? 'bg-green-100 text-green-700'
                : profile.subscription_status === 'trial' ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
              }`}>
                {profile.subscription_status === 'active' ? 'Active' : profile.subscription_status === 'trial' ? 'Trial' : 'Expired'}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-2">Carrier limit: {profile.carrier_limit || 20}</p>
            {profile.trial_ends_at && profile.subscription_status === 'trial' && (
              <p className="text-sm text-yellow-600 mt-1">Trial ends: {new Date(profile.trial_ends_at).toLocaleDateString()}</p>
            )}
          </div>
          <a href="/pricing" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
            {profile.subscription_status === 'active' ? 'Manage Plan' : 'Upgrade Plan'}
          </a>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white shadow rounded-lg p-6 border border-red-200">
        <h2 className="text-lg font-semibold text-red-700 mb-2">Danger Zone</h2>
        <p className="text-sm text-gray-600 mb-4">These actions are permanent and cannot be undone.</p>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 border border-red-100 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900">Deactivate Company</p>
              <p className="text-xs text-gray-500">Contact support to deactivate your company account</p>
            </div>
            <a href="mailto:support@loaddrive.com?subject=Account Deactivation Request" className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm font-medium">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}